/**
 * Visibility driver for image effect overlays. Two tiers, so only what can be seen costs
 * frames while what is about to be seen is already built:
 *
 * - warm: the overlay is within `WARM_MARGIN` of the viewport. The worker, core chunk and
 *   canvas are created and one still frame is drawn, but nothing ticks — scrolling it in
 *   shows a ready picture instead of waiting on worker spawn plus chunk load.
 * - live: the overlay intersects the viewport. Only now does the frame loop run.
 *
 * Leaving the viewport pauses the loop immediately. Leaving the warm zone releases the
 * worker after a grace window, so a fast scroll through a gallery does not tear down and
 * rebuild a worker per row.
 *
 * There is no concurrency cap on warm overlays; the per-overlay guards (minimum box size,
 * reduced motion) live in `EffectOverlay.client.tsx`.
 */

export type EffectVisibilityHandlers = {
  /** Near the viewport: build the effect (paused). */
  warm: () => void;
  /** Gone from the warm zone for the grace window: release it. */
  cool: () => void;
  /** In the viewport: run the frame loop. */
  play: () => void;
  /** Out of the viewport: stop the frame loop, keep the effect built. */
  pause: () => void;
};

type Subscriber = EffectVisibilityHandlers & {
  nearby: boolean;
  warmed: boolean;
  playing: boolean;
  coolTimer: ReturnType<typeof setTimeout> | null;
};

/**
 * How far outside the viewport an effect is already built: one viewport height in each
 * direction, so it is warm at any realistic scroll speed and scrolling back up is covered.
 * Kept to one viewport (not more) because every warm overlay holds a worker and a canvas.
 */
const WARM_MARGIN = "100% 0px";

/** Time out of the warm zone before the worker is torn down. */
const COOL_GRACE_MS = 1500;

const subscribers = new Map<Element, Subscriber>();
let warmObserver: IntersectionObserver | null = null;
let liveObserver: IntersectionObserver | null = null;

const clearCoolTimer = (subscriber: Subscriber) => {
  if (!subscriber.coolTimer) return;
  clearTimeout(subscriber.coolTimer);
  subscriber.coolTimer = null;
};

const warmUp = (subscriber: Subscriber) => {
  clearCoolTimer(subscriber);
  if (subscriber.warmed) return;
  subscriber.warmed = true;
  subscriber.warm();
};

const coolDown = (subscriber: Subscriber) => {
  clearCoolTimer(subscriber);
  if (!subscriber.warmed) return;
  pauseSubscriber(subscriber);
  subscriber.warmed = false;
  subscriber.cool();
};

const playSubscriber = (subscriber: Subscriber) => {
  if (subscriber.playing) return;
  subscriber.playing = true;
  subscriber.play();
};

const pauseSubscriber = (subscriber: Subscriber) => {
  if (!subscriber.playing) return;
  subscriber.playing = false;
  subscriber.pause();
};

const onWarmIntersect: IntersectionObserverCallback = (entries) => {
  for (const entry of entries) {
    const subscriber = subscribers.get(entry.target);
    if (!subscriber) continue;
    subscriber.nearby = entry.isIntersecting;
    if (entry.isIntersecting) {
      warmUp(subscriber);
      continue;
    }
    if (!subscriber.warmed) continue;
    clearCoolTimer(subscriber);
    subscriber.coolTimer = setTimeout(() => {
      subscriber.coolTimer = null;
      if (!subscriber.nearby) coolDown(subscriber);
    }, COOL_GRACE_MS);
  }
};

const onLiveIntersect: IntersectionObserverCallback = (entries) => {
  for (const entry of entries) {
    const subscriber = subscribers.get(entry.target);
    if (!subscriber) continue;
    if (!entry.isIntersecting) {
      pauseSubscriber(subscriber);
      continue;
    }
    // Entering the viewport implies being near it; the two observers report
    // independently, so make sure the effect exists before it is asked to play.
    warmUp(subscriber);
    playSubscriber(subscriber);
  }
};

/**
 * Registers an overlay. The returned release must run on unmount; it tears the effect
 * down if it is still built.
 */
export const watchEffectVisibility = (element: Element, handlers: EffectVisibilityHandlers) => {
  if (typeof IntersectionObserver === "undefined") return () => {};

  const subscriber: Subscriber = {
    ...handlers,
    nearby: false,
    warmed: false,
    playing: false,
    coolTimer: null,
  };
  subscribers.set(element, subscriber);

  warmObserver ??= new IntersectionObserver(onWarmIntersect, {
    rootMargin: WARM_MARGIN,
    threshold: 0,
  });
  liveObserver ??= new IntersectionObserver(onLiveIntersect, { threshold: 0 });
  warmObserver.observe(element);
  liveObserver.observe(element);

  return () => {
    warmObserver?.unobserve(element);
    liveObserver?.unobserve(element);
    subscribers.delete(element);
    coolDown(subscriber);
  };
};
